import { type z } from 'zod';

import { type OpcaoPesquisavel } from '@/ui/components/select-pesquisavel';
import {
  type MODELOS_REGRA_CLASSIFICACAO,
  type PreviaRegra,
  type RegraClassificacao,
  type regraClassificacaoEntradaSchema,
} from '@fiscoai/contracts';

import { opcaoCentroCusto } from './api';
import { itens } from './textos';

/** Valores do formulário (entrada do schema: listas e `ativa` podem faltar até validar). */
export type FormRegra = z.input<typeof regraClassificacaoEntradaSchema>;

/** O que o editor recebe ao abrir: os valores e o centro de custo já escolhido (para exibir). */
export interface Rascunho {
  /** Nulo: regra nova. */
  regraId: string | null;
  valores: Partial<FormRegra>;
  centroCusto: OpcaoPesquisavel | null;
  /** Classificação da empresa já escolhida (para exibir mesmo se desativada). */
  personalizada: { id: string; nome: string } | null;
}

const SEM_CONDICOES = {
  ncmPrefixos: [],
  cfopsFornecedor: [],
  cnpjsEmitente: [],
  descricaoContem: [],
};

export function rascunhoNovo(): Rascunho {
  return {
    regraId: null,
    valores: {
      nome: '',
      ativa: true,
      condicoes: SEM_CONDICOES,
      classificacaoPersonalizadaId: null,
      centroCustoId: null,
    },
    centroCusto: null,
    personalizada: null,
  };
}

/** Classificação da empresa levada da aba Classificações para uma regra nova. */
export interface DestinoRegra {
  id: string;
  nome: string;
}

/** Regra nova que já sabe o destino ("Usar como destino de uma regra"). */
export function rascunhoComDestino(destino: DestinoRegra): Rascunho {
  const novo = rascunhoNovo();
  return {
    ...novo,
    valores: {
      ...novo.valores,
      classificacao: 'PERSONALIZADA',
      classificacaoPersonalizadaId: destino.id,
    },
    personalizada: destino,
  };
}

export function rascunhoDoModelo(modelo: (typeof MODELOS_REGRA_CLASSIFICACAO)[number]): Rascunho {
  return {
    regraId: null,
    valores: {
      nome: modelo.nome,
      ativa: true,
      classificacao: modelo.classificacao,
      condicoes: {
        ncmPrefixos: [...modelo.condicoes.ncmPrefixos],
        cfopsFornecedor: [...modelo.condicoes.cfopsFornecedor],
        cnpjsEmitente: [...modelo.condicoes.cnpjsEmitente],
        descricaoContem: [...modelo.condicoes.descricaoContem],
      },
      classificacaoPersonalizadaId: null,
      centroCustoId: null,
    },
    centroCusto: null,
    personalizada: null,
  };
}

export function rascunhoDaRegra(regra: RegraClassificacao): Rascunho {
  return {
    regraId: regra.id,
    valores: {
      nome: regra.nome,
      ativa: regra.ativa,
      classificacao: regra.classificacao,
      condicoes: regra.condicoes,
      classificacaoPersonalizadaId: regra.personalizada?.id ?? null,
      centroCustoId: regra.centroCusto?.id ?? null,
    },
    centroCusto: regra.centroCusto ? opcaoCentroCusto(regra.centroCusto) : null,
    personalizada: regra.personalizada,
  };
}

/** Quantos itens a regra classificaria de fato: os que casam, menos os manuais e os de cima. */
export function liquidoDaPrevia(previa: PreviaRegra): number {
  return Math.max(0, previa.itens - previa.jaManuais - previa.pegosPorRegraAcima);
}

/** "Regra salva. 47 itens classificados." — o mesmo verbo do botão. */
export function mensagemSalva(ativa: boolean, classificados: number): string {
  if (!ativa) return 'Regra salva. Desligada, não classifica nenhum item.';
  if (classificados === 0) return 'Regra salva. Ela vale para as próximas notas.';
  const verbo = classificados === 1 ? 'classificado' : 'classificados';
  return `Regra salva. ${itens(classificados)} ${verbo}.`;
}
